// Bridge between the module-scoped API client and the React-scoped AuthContext.
//
// The shared API client (`@liquid-spirit/api` createApiClient) is created once at
// module load, but the session refresh/expiry logic lives inside AuthContext.
// This tiny registry lets the client call into the *current* AuthContext without
// importing React state, so there is exactly ONE refresh path for the whole app:
// a 401 on any request -> client calls refreshSession() -> AuthContext.attemptRefresh
// (deduped) -> client retries the original request once with the fresh token.
//
// AuthContext registers its handlers on mount (see setAuthBridge) and clears them
// on unmount.

const bridge = {
  refreshSession: null,
  expireSession: null,
};

export const setAuthBridge = ({ refreshSession = null, expireSession = null } = {}) => {
  bridge.refreshSession = refreshSession;
  bridge.expireSession = expireSession;
  return () => {
    // An older provider's cleanup must not unregister a newer provider.
    if (bridge.refreshSession === refreshSession) bridge.refreshSession = null;
    if (bridge.expireSession === expireSession) bridge.expireSession = null;
  };
};

// Called by the API client when a request comes back as an expired/unauthenticated
// 401. Resolves to `{ token }` so the client can retry with the new access token.
// Throws when there is no session to refresh (or the refresh failed) so the client
// falls through to expireSession() instead of retrying a doomed request.
export const refreshSessionViaBridge = async () => {
  if (typeof bridge.refreshSession !== 'function') {
    throw new Error('No active session to refresh');
  }
  const result = await bridge.refreshSession();
  if (!result || !result.token) {
    throw new Error('Session refresh failed');
  }
  return result;
};

export const expireSessionViaBridge = (reason) => {
  if (typeof bridge.expireSession === 'function') {
    bridge.expireSession(reason || 'Session expired');
  }
};
